import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';
import { PaginatedResultModel, PaginationSearchModel } from '@data-layer/models/common';
import { QuoteManualDetailedModel, QuoteManualModel, QuoteModel, QuoteUpdateModel, SurveyInfoModel } from '@data-layer/models/lcl';
import {
  ManagementManualQuoteCountByStateModel,
  ManagementManualQuoteModel,
  ManagementManualQuoteSearchModel,
  ManagementQuoteModel,
  ManagementQuoteSearchModel,
} from '@data-layer/models/management';

@Injectable({
  providedIn: 'root',
})
export class QuoteRepository {
  constructor(private apiService: ApiService) {}

  // TODO: investigate QuoteModel and LclQuoteCardModel interfaces
  public search(companyId: number, params?: PaginationSearchModel, isShowLoader?: boolean): Observable<PaginatedResultModel<any>> {
    return this.apiService.get<PaginatedResultModel<any>>(
      `${environment.url.prefixFreight}companies/${companyId}/quotes`,
      { params },
      { isShowLoader },
    );
  }

  public searchManual(
    companyId: number,
    params?: PaginationSearchModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<QuoteManualDetailedModel>> {
    return this.apiService.get<PaginatedResultModel<QuoteManualDetailedModel>>(
      `${environment.url.prefixFreight}companies/${companyId}/manual-quotes`,
      { params },
      { isShowLoader },
    );
  }

  public get(id: number, isShowLoader?: boolean): Observable<QuoteModel> {
    return this.apiService.get<QuoteModel>(`${environment.url.freight.quotes}/${id}`, null, { isShowLoader });
  }

  public delete(id: number, isShowLoader?: boolean): Observable<any> {
    return this.apiService.delete<any>(`${environment.url.freight.quotes}/${id}`, null, { isShowLoader });
  }

  public create(quote: QuoteUpdateModel, token?: string, isShowLoader?: boolean): Observable<QuoteModel> {
    const options: any = !!token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : null;

    return this.apiService.post<QuoteModel>(`${environment.url.prefixFreight}companies/${quote.companyId}/quotes`, quote, options, {
      isShowLoader,
    });
  }

  public createManual(companyId: number, quote: QuoteManualModel, token?: string, isShowLoader?: boolean): Observable<QuoteManualModel> {
    const options: any = !!token
      ? {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      : null;

    return this.apiService.post<QuoteManualModel>(`${environment.url.prefixFreight}companies/${companyId}/manual-quotes`, quote, options, {
      isShowLoader,
    });
  }

  public createExternalManual(quote: QuoteManualModel, recaptchaToken: string, isShowLoader?: boolean): Observable<QuoteManualModel> {
    return this.apiService.post<QuoteManualModel>(
      `${environment.url.prefixFreight}external-manual-quotes`,
      quote,
      {
        headers: {
          'x-recaptcha-token': recaptchaToken,
        },
      },
      {
        isShowLoader,
      },
    );
  }

  public update(quote: QuoteUpdateModel, isShowLoader?: boolean): Observable<QuoteModel> {
    return this.apiService.put<QuoteModel>(`${environment.url.freight.quotes}/${quote.id}`, quote, null, { isShowLoader });
  }

  public updateVoyage(quoteId: number, voyageId: number, isShowLoader?: boolean): Observable<QuoteModel> {
    return this.apiService.put<QuoteModel>(`${environment.url.freight.quotes}/${quoteId}/voyageId`, voyageId, null, {
      isShowLoader,
    });
  }

  public searchManagement(
    params?: ManagementQuoteSearchModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<ManagementQuoteModel>> {
    return this.apiService.get<PaginatedResultModel<ManagementQuoteModel>>(
      environment.url.freight.quotesManagement,
      { params },
      { isShowLoader },
    );
  }

  public searchManualManagement(
    params?: ManagementManualQuoteSearchModel,
    isShowLoader?: boolean,
  ): Observable<PaginatedResultModel<ManagementManualQuoteModel>> {
    return this.apiService.get<PaginatedResultModel<ManagementManualQuoteModel>>(
      environment.url.freight.quotesManualManagement,
      { params },
      { isShowLoader },
    );
  }

  public getManagement(id: ManagementQuoteModel['id'], isShowLoader?: boolean): Observable<ManagementQuoteModel> {
    return this.apiService.get<ManagementQuoteModel>(`${environment.url.freight.quotesManagement}/${id}`, null, { isShowLoader });
  }

  public getManagementUserData(id: number, isShowLoader?: boolean): Observable<QuoteModel> {
    return this.apiService.get<QuoteModel>(`${environment.url.freight.quotesManagement}/${id}/user-data`, null, { isShowLoader });
  }

  public updateManagement(model: ManagementQuoteModel, isShowLoader?: boolean): Observable<ManagementQuoteModel> {
    return this.apiService.put<ManagementQuoteModel>(`${environment.url.freight.quotesManagement}/${model.id}`, model, null, {
      isShowLoader,
    });
  }

  public updateManualManagement(
    model: Partial<ManagementManualQuoteModel>,
    isShowLoader?: boolean,
  ): Observable<ManagementManualQuoteModel> {
    return this.apiService.put<ManagementManualQuoteModel>(`${environment.url.freight.quotesManualManagement}/${model.id}`, model, null, {
      isShowLoader,
    });
  }

  public deleteManagement(id: ManagementQuoteModel['id'], isShowLoader?: boolean): Observable<any> {
    return this.apiService.delete<any>(`${environment.url.freight.quotesManagement}/${id}`, null, { isShowLoader });
  }

  public getManagementCountStats(isShowLoader?: boolean): Observable<ManagementManualQuoteCountByStateModel[]> {
    return this.apiService.get<ManagementManualQuoteCountByStateModel[]>(
      `${environment.url.freight.quotesManualManagement}/count-by-status`,
      null,
      { isShowLoader },
    );
  }

  public attachManualQuoteToVoyageManagement(quoteId: number, voyageId: number, isShowLoader?: boolean): Observable<ManagementQuoteModel> {
    return this.apiService.post<ManagementQuoteModel>(
      `${environment.url.freight.quotesManualManagement}/${quoteId}/convert-to-quote`,
      voyageId,
      null,
      {
        isShowLoader,
      },
    );
  }

  public submitExitIntentAnswer(info: SurveyInfoModel, isShowLoader?: boolean): Observable<SurveyInfoModel> {
    return this.apiService.post<SurveyInfoModel>(`${environment.url.prefixFreight}v1/survey-infos`, info, null, {
      isShowLoader,
    });
  }
}
